import type { QueryClient } from "@tanstack/svelte-query";
import { isEqual, uniqBy } from "lodash-es";
import type { Readable } from "svelte/store";
import { get } from "svelte/store";
import { assign, createMachine, type ActorRefFrom, type StateFrom } from "xstate";

import type {
  EditableLocalFields,
  EditableMetadata,
  Image,
  PersonInImage,
} from "bildebanken-model";
import { ROLES, calculateRightsMarker, isPersonInImage } from "bildebanken-model";
import { getMetadataItem, updateMetadata, type ImagePatchWithId } from "services/api";
import { authorityToContact, currentUserAuthority } from "services/authority";
import { upsertMimirImage, upsertMimirSearchResultsWithImage } from "state/queries/mimir";
import { getFailedUploads, isUploadInProgress, type UploadsState } from "state/upload";
import { enrichMetadataFromIptc, enrichTechnicalMetadataFromIptc, getIptc } from "utils/exif-iptc";
import { diffingDerivedStore, validDateTime } from "utils/fns";
import { getDefaultVisibility } from "./visibility";
import type { SearchHit } from "services/searchHit";

export type EditableMetadataFieldKey = keyof EditableMetadata;
export type EditableLocalFieldKey = keyof EditableLocalFields;

export const fieldLabels: Partial<Record<EditableMetadataFieldKey, string>> = {
  title: "Tittel",
  rights: "Vilkår",
  digitalSourceType: "Bildetype",
  altText: "Alternativ tekst",
  description: "Beskrivelse",
};

type EditedImage = Image;
export type EditImageContext = {
  imageIds: string[];
  /** keep original state to be able to compare changes */
  originals: Image[];
  /** for display, originals with changes applied */
  images: EditedImage[];
  /** will eventually be sent to server */
  changes: ImagePatchWithId[];
  /** precomputed map of image ID to index for O(1) lookups */
  imageIdToIndexMap: Map<string, number>;
  current: number;
  selectedIds: string[];
  faceDetectionError?: string;
  requiredFields: EditableMetadataFieldKey[];
  failedUploadImageIds: string[];
  uploads?: Readable<UploadsState>;
  client?: QueryClient;
  error?: Error;
};

type EditMetadataEvent = {
  type: "EDIT_METADATA";
  field: keyof EditableMetadata;
  value: EditableMetadata[keyof EditableMetadata];
};

type EditVisibilityEvent = {
  type: "EDIT_VISIBILITY";
  value: ImagePatchWithId["visibleTo"];
};

type UpdateDetectedLabelsEvent = {
  type: "UPDATE_DETECTED_LABELS";
  imageId: string;
  detectedLabels: NonNullable<Image["detectedLabels"]>;
};

export type EditPersonEvent =
  | {
      type: "EDIT_PERSON";
      resId: string;
      field: "detectedFace.hidden" | "detectedFace.verified";
      value: boolean;
    }
  | { type: "EDIT_PERSON"; resId: string; field: "detectedFace.personId"; value: string }
  | {
      type: "EDIT_PERSON";
      resId: string;
      field: "contact";
      value: { resId: string; title: string };
    };

type EditImageEvent =
  | { type: "SET_IMAGES"; imageIds: string[] }
  | { type: "SELECT_IMAGE"; imageId: string }
  | { type: "SELECT_MULTIPLE_IMAGES"; imageId: string }
  | { type: "SELECT_ALL" }
  | { type: "FACE_DETECTION_ERROR"; message: string }
  | { type: "OPEN_EDIT_PEOPLE_IN_IMAGE" }
  | { type: "CLOSE_EDIT_PEOPLE_IN_IMAGE" }
  | { type: "RESET_FACES_FOR_CURRENT_IMAGE" }
  | EditPersonEvent
  | EditMetadataEvent
  | EditVisibilityEvent
  | UpdateDetectedLabelsEvent
  | { type: "UPLOADS_FAILED"; imageIds: string[] }
  | { type: "SUBMIT" };

/**** Helpers ****/
const buildImageIdToIndexMap = (images: EditedImage[]): Map<string, number> => {
  const map = new Map<string, number>();
  images.forEach((image, index) => {
    map.set(image.id, index);
  });
  return map;
};

const processImageForInitialization = (
  image: Image,
  uploads?: Readable<UploadsState>,
): EditedImage => {
  const result = { ...image };

  // if description contains only whitespaces replace with empty string
  if (image.metadata.description && image.metadata.description.trim().length === 0) {
    image.metadata.description = "";
  }

  if (uploads && isUploadInProgress(get(uploads))) {
    result.metadata.rightsMarker = "Free";
    // set uploaded
    if (currentUserAuthority) {
      const contact = authorityToContact(currentUserAuthority);
      result.metadata = {
        ...image.metadata,
        contributors: [{ contact, role: { resId: ROLES.uploader } }],
      };
    }

    // Try to get some data from IPTC
    const iptc = getIptc(image.id);
    if (iptc) {
      result.metadata = enrichMetadataFromIptc(iptc, result.metadata);
      result.technicalMetadata = enrichTechnicalMetadataFromIptc(
        iptc,
        image.mediaType,
        result.technicalMetadata,
      );
      result.thumbnail = iptc.thumbnail
        ? URL.createObjectURL(new Blob([iptc.thumbnail] as BlobPart[]))
        : undefined;
    }

    // Set default visibility fo everyone
    result.visibleTo = getDefaultVisibility();
  }

  return result;
};

/**** Actions ****/
const assignFieldForSelectedImages = assign<EditImageContext, EditMetadataEvent>({
  images: (context, event) => {
    const copy = context.images.slice();
    for (const id of context.selectedIds) {
      const idx = context.imageIdToIndexMap.get(id);
      if (idx !== undefined) {
        copy[idx] = {
          ...copy[idx],
          metadata: { ...copy[idx].metadata, [event.field]: event.value },
        };
      }
    }
    return copy;
  },
  changes: (context, event) => {
    const copy = context.changes.slice();
    for (const id of context.selectedIds) {
      const idx = context.imageIdToIndexMap.get(id);
      if (idx !== undefined) {
        copy[idx] = {
          ...copy[idx],
          metadataDelta: { ...copy[idx].metadataDelta, [event.field]: event.value },
        };
        if (
          copy[idx].metadataDelta![event.field] === context.originals[idx].metadata[event.field]
        ) {
          delete copy[idx].metadataDelta![event.field];
        }
      }
    }
    return copy;
  },
});

const assignVisibilityForSelectedImages = assign<EditImageContext, EditVisibilityEvent>({
  images: (context, event) => {
    const copy = context.images.slice();
    for (const id of context.selectedIds) {
      const idx = context.imageIdToIndexMap.get(id);
      if (idx !== undefined) {
        copy[idx] = { ...copy[idx], visibleTo: event.value };
      }
    }
    return copy;
  },
  changes: (context, event) => {
    const copy = context.changes.slice();
    for (const id of context.selectedIds) {
      const idx = context.imageIdToIndexMap.get(id);
      if (idx !== undefined) {
        copy[idx] = { ...copy[idx], visibleTo: event.value };
        if (isEqual(copy[idx].visibleTo, context.originals[idx].visibleTo)) {
          delete copy[idx].visibleTo;
        }
      }
    }
    return copy;
  },
});

const assignDetectedLabels = assign<EditImageContext, UpdateDetectedLabelsEvent>({
  images: (context, event) =>
    context.images.map((image) =>
      image.id === event.imageId ? { ...image, detectedLabels: event.detectedLabels } : image,
    ),
});

/** Selectors **/

export const getCurrentImage = ({ context: { current, images } }: EditImageState) =>
  images[current];
export const getPeopleInCurrentImage = ({
  context: { current, images },
}: EditImageState): PersonInImage[] =>
  images[current]?.metadata.contributors?.filter(isPersonInImage) ?? [];

export const getAllEditedImages = ({ context }: EditImageState): Image[] => context.images;
export const getAllChanges = ({ context }: EditImageState): ImagePatchWithId[] => context.changes;
export const getSelectedIds = ({ context }: EditImageState): string[] => context.selectedIds;

/**
 * Sends an event to service to update the value of a given field.
 *
 * (I wasn't able to get field/value type inference to work when calling service.send()
 * directly from components. This helper function solves that.)
 */
export function updateField<K extends EditableMetadataFieldKey>(
  service: EditImageActor,
  field: K,
  value: EditableMetadata[K],
) {
  service.send({
    type: "EDIT_METADATA",
    field,
    value,
  });
}

export type EditImageState = StateFrom<typeof editImageMachine>;
export type EditImageActor = ActorRefFrom<typeof editImageMachine>;

type GetMetadataServiceResponse = { type: string; data: Image[] };

const assignInitialData = assign<EditImageContext, GetMetadataServiceResponse>((context, event) => {
  const { images, uploads } = context;
  const { data } = event;
  const processedImages = uniqBy(data.concat(images), "id").map((image) =>
    processImageForInitialization(image, uploads),
  );

  return {
    images: processedImages,
    originals: uniqBy(data.concat(context.originals), "id"),
    imageIdToIndexMap: buildImageIdToIndexMap(processedImages),
  };
});

const initializeChanges = assign<EditImageContext, EditImageEvent>({
  changes: ({ images, uploads }) => {
    const uploading = uploads && isUploadInProgress(get(uploads));
    return images.map((image) => {
      const patch: ImagePatchWithId = { id: image.id, metadataDelta: {} };

      if (uploading) {
        // Make sure upload default data are recorded as changes
        patch.metadataDelta = image.metadata;
        patch.visibleTo = image.visibleTo;
      }

      return patch;
    });
  },
});

/** Machine **/

export const editImageMachine = createMachine<EditImageContext, EditImageEvent>({
  id: "edit",
  initial: "Loading",
  predictableActionArguments: true,
  on: {
    UPLOADS_FAILED: {
      actions: assign({
        failedUploadImageIds: (_, event) => event.imageIds,
      }),
    },
  },
  // In case editing is in context of and upload, connect to uploads store
  // and keep track of failed uploads to avoid trying to update their metadata when saving.
  invoke: {
    src: (context) => (send) => {
      if (!context.uploads) return;
      const failedUploadIds = diffingDerivedStore(context.uploads, (state) =>
        getFailedUploads(state)
          .map((upload) => upload.id)
          .filter(Boolean),
      ) as Readable<string[]>;

      return failedUploadIds.subscribe((imageIds) => {
        send({ type: "UPLOADS_FAILED", imageIds });
      });
    },
  },
  states: {
    Loading: {
      invoke: {
        src: (context) =>
          Promise.all(context.imageIds.map((value) => getMetadataItem(value, "Bildebanken"))),
        onDone: [
          {
            actions: assignInitialData,
            target: "Editing",
          },
        ],
        onError: {
          target: "Error",
          actions: [
            assign({
              error: (context, event) =>
                event.data && event.data instanceof Error ? event.data : new Error("Ukjent feil"),
            }),
          ],
        },
      },
    },
    Editing: {
      initial: "Determining",
      on: {
        SUBMIT: "Saving",
        SELECT_ALL: {
          target: "Editing.Multiple",
          internal: true,
          actions: [
            assign({
              selectedIds: ({ images }) => images.map(({ id }) => id),
            }),
          ],
        },
        SELECT_IMAGE: {
          target: "Editing.Single",
          internal: true,
          actions: [
            assign({
              current: ({ images }, { imageId }) => images.findIndex(({ id }) => id === imageId),
              selectedIds: (_, { imageId }) => [imageId],
            }),
          ],
        },
        UPDATE_DETECTED_LABELS: {
          actions: [assignDetectedLabels],
        },
      },
      entry: [initializeChanges],
      states: {
        Determining: {
          always: [
            { cond: ({ images }) => images.length === 1, target: "Single" },
            { target: "Multiple" },
          ],
        },
        Multiple: {
          on: {
            SELECT_MULTIPLE_IMAGES: {
              target: "Single",
              actions: [
                assign({
                  selectedIds: ({ selectedIds }, { imageId }) => {
                    const remaining = selectedIds.filter((id) => id !== imageId);
                    return remaining.length > 0 ? remaining : selectedIds;
                  },
                  current: ({ imageIdToIndexMap, selectedIds }, { imageId }) => {
                    return getCurrentImageFromSelectedIds(selectedIds, imageId, imageIdToIndexMap);
                  },
                }),
              ],
            },
            EDIT_METADATA: {
              actions: [assignFieldForSelectedImages],
            },
            EDIT_VISIBILITY: {
              actions: [assignVisibilityForSelectedImages],
            },
          },
        },
        Single: {
          entry: [assign({ faceDetectionError: () => undefined })],
          on: {
            SELECT_MULTIPLE_IMAGES: [
              {
                cond: ({ selectedIds, images }, { imageId }) =>
                  !selectedIds.includes(imageId) &&
                  images.length > 1 &&
                  selectedIds.length + 1 === images.length,
                target: "Multiple",
                actions: [
                  assign({
                    selectedIds: ({ selectedIds }, { imageId }) => [...selectedIds, imageId],
                    current: ({ images }, { imageId }) =>
                      images.findIndex(({ id }) => id === imageId),
                  }),
                ],
              },
              {
                actions: [
                  assign({
                    selectedIds: ({ selectedIds }, { imageId }) => {
                      if (selectedIds.includes(imageId)) {
                        const remaining = selectedIds.filter((id) => id !== imageId);
                        return remaining.length > 0 ? remaining : selectedIds;
                      }
                      return [...selectedIds, imageId];
                    },
                    current: ({ imageIdToIndexMap, selectedIds }, { imageId }) => {
                      if (selectedIds.includes(imageId)) {
                        return getCurrentImageFromSelectedIds(
                          selectedIds,
                          imageId,
                          imageIdToIndexMap,
                        );
                      }
                      // Checking: set current to the newly selected item
                      return imageIdToIndexMap.get(imageId) ?? 0;
                    },
                  }),
                ],
              },
            ],
            EDIT_METADATA: {
              actions: [assignFieldForSelectedImages],
            },
            EDIT_VISIBILITY: {
              actions: [assignVisibilityForSelectedImages],
            },
          },
        },
      },
    },
    Saving: {
      invoke: {
        src: (context) =>
          Promise.all(
            context.changes
              .filter(
                (patch) =>
                  (patch.metadataDelta && Object.keys(patch.metadataDelta).length > 0) ||
                  patch.visibleTo,
              )
              .filter((patch) => !context.failedUploadImageIds.includes(patch.id))
              .map((patch) =>
                updateMetadata(patch).then((image) => {
                  upsertMimirImage(context.client, image);
                  upsertMimirSearchResultsWithImage(context.client, image);
                }),
              ),
          ),
        onDone: "Done",
        onError: {
          target: "Editing",
          actions: [
            assign({
              error: (context, event) =>
                event.data && event.data instanceof Error ? event.data : new Error("Ukjent feil"),
            }),
          ],
        },
      },
    },
    Done: {},
    Error: {},
  },
});

function getCurrentImageFromSelectedIds(
  selectedIds: string[],
  imageId: string,
  imageIdToIndexMap: Map<string, number>,
) {
  // Unchecking: set current to first remaining selected item
  const remaining = selectedIds.filter((id) => id !== imageId);
  if (remaining.length > 0) {
    return imageIdToIndexMap.get(remaining[0]) ?? 0;
  }
  // If no items remaining, keep current
  return imageIdToIndexMap.get(imageId) ?? 0;
}

export function valid(image: Image) {
  return !image.metadata.mediaCreatedOn || validDateTime(image.metadata.mediaCreatedOn);
}

export function validDate(state: EditImageState): boolean {
  return !!state.context.images.some((image) => valid(image));
}

export function fieldMissingInImage(image: Image | SearchHit, field: EditableMetadataFieldKey) {
  const contentInField = image.metadata[field];
  if (!contentInField) return true;
  if ((field === "title" || field === "altText") && contentInField.trim().length === 0) {
    return true;
  }
  if (field === "rights" && "rights" in image.metadata) {
    const rightsMarker =
      image.metadata.rightsMarker ||
      calculateRightsMarker(image.metadata.rights, image.metadata.instructions);
    if (
      rightsMarker === "Restricted" &&
      !image.metadata.rights?.note &&
      !image.metadata.instructions
    )
      return true;
    if (rightsMarker === "Free" && !image.metadata.rights?.rightsHolder) return true;
  }
  return false;
}

export function requiredFieldsMissing(state: EditImageState): boolean {
  for (const field of state.context.requiredFields) {
    if (state.context.images.some((image) => fieldMissingInImage(image, field))) {
      return true;
    }
  }
  return false;
}

export function requiredFieldsMissingInImage(
  state: EditImageState,
  itemId: string,
): EditableMetadataFieldKey[] {
  const image = state.context.images.find((image) => image.id === itemId);

  if (!image) return [];

  return state.context.requiredFields.filter((field) => fieldMissingInImage(image, field));
}

export function isRequiredField(state: EditImageState, field: EditableMetadataFieldKey) {
  return state.context.requiredFields.includes(field);
}

export function fieldMissingForCurrentImage(
  state: EditImageState,
  field: EditableMetadataFieldKey,
) {
  return fieldMissingInImage(state.context.images[state.context.current], field);
}
